"use client";
import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslations } from "next-intl";
import {
   PlusIcon,
   Search,
   HatGlasses,
   ChevronDown,
   ChevronLeft,
   ChevronRight
} from "lucide-react";
import { useRouter } from "@/i18n/navigation";
import {
   Button,
   Skeleton,
   ScrollArea,
   Loading,
   CustomTooltip
} from "@/app/components/ui";
import { TemporaryChatToggle } from "@/app/components/ui/TemporaryChatToggle";
import { ModeToggle } from "@/app/components/ui/Button-mode-toggle";
import { LanguageSwitcher } from "@/app/components/ui/Language-switcher";
import { ProfileMenu } from "@/app/components/ui/Profile-menu";
import { UsageIndicator } from "@/app/components/ui/UsageIndicator";
import { ChatHistory as ChatHistoryType } from "@/types";
import { useAuth, useMediaQuery } from "@/app/hooks";
import { useStateHistoryChatPage } from "@/app/store/chat";
import { ChatHistoryItem } from "./ChatHistoryItem";
import { cn, compareChatHistoryOrder, mergeChatHistories } from "@/app/lib/utils";
import { useAiSourceSelector } from "@/app/store/ai-source-selector";
import { PROJECTS } from "@/app/lib/enums";
import { MAX_PINNED_CHAT_HISTORIES } from "@/app/lib/constants";

interface Props {
   isOpen: boolean;
   onOpenChange: (open: boolean) => void;
   sidebarWidth: number;
   onSidebarWidthChange: (width: number) => void;
   isResizing: boolean;
   onResizeStateChange: (isResizing: boolean) => void;
   className?: string;
   shareKey?: string;
   studentId?: string;
   hiddenNewChat?: boolean;
}

const MIN_SIDEBAR_WIDTH = 240;
const DEFAULT_SIDEBAR_WIDTH = 300;
const MAX_SIDEBAR_WIDTH = 520;
const SIDEBAR_VIEWPORT_GUTTER = 80;

function clampSidebarWidth(width: number) {
   const max =
      typeof window === "undefined"
         ? MAX_SIDEBAR_WIDTH
         : Math.max(MIN_SIDEBAR_WIDTH, window.innerWidth - SIDEBAR_VIEWPORT_GUTTER);
   return Math.min(Math.max(width, MIN_SIDEBAR_WIDTH), MAX_SIDEBAR_WIDTH, max);
}

const ITEM_PER_PAGE = 25;

export function ChatHistory({
   isOpen,
   onOpenChange,
   sidebarWidth,
   onSidebarWidthChange,
   isResizing,
   onResizeStateChange,
   className,
   shareKey,
   studentId,
   hiddenNewChat = false
}: Props) {
   const router = useRouter();
   const translate = useTranslations("Home");
   const isMobile = useMediaQuery("(max-width: 768px)");
   const isTablet = useMediaQuery("(min-width: 769px) and (max-width: 1024px)");
   const { user } = useAuth();
   const [historyList, setHistoryList] = useState<ChatHistoryType[]>([]);
   const [nextCursor, setNextCursor] = useState<string | null>(null);
   const [loading, setLoading] = useState(false);
   const {
      historyItem,
      loadMoreHistories,
      patchChatHistory,
      removeChatHistory,
      setHistoryItem,
      shouldRefetchHistories,
      pendingHistoryPatch,
      resetState,
      isTemporaryChat,
      isPinnedSectionOpen,
      setPinnedSectionOpen,
      isRecentSectionOpen,
      setRecentSectionOpen
   } = useStateHistoryChatPage();
   const historyRef = useRef<HTMLDivElement>(null);
   const fetchGenerationRef = useRef(0);
   const previousScrollTopRef = useRef(0);
   const { selectedBaseKnowledge } = useAiSourceSelector();
   const isCompactLayout = isMobile || isTablet;
   const effectiveSidebarWidth = isCompactLayout ? DEFAULT_SIDEBAR_WIDTH : sidebarWidth;

   const fetchHistories = useCallback(
      async (options?: { after?: string; keepLoaded?: boolean }) => {
         if (!user?.id) return;
         const { after, keepLoaded } = options ?? {};
         const generation = ++fetchGenerationRef.current;
         if (!after && !keepLoaded) setNextCursor(null);
         setLoading(true);
         try {
            const knowledgeBase = selectedBaseKnowledge.id
               ? [selectedBaseKnowledge.id as PROJECTS]
               : [(user?.projects?.[0] as PROJECTS) ?? []];
            const data = await loadMoreHistories(
               studentId || user.id,
               knowledgeBase,
               after,
               ITEM_PER_PAGE
            );
            if (generation !== fetchGenerationRef.current) return;
            const items = (data?.items ?? []).sort(compareChatHistoryOrder);
            if (!after && !keepLoaded) {
               setHistoryList(items);
            } else if (items.length) {
               setHistoryList((prev) => mergeChatHistories(prev, items));
            }
            if (!keepLoaded) setNextCursor(data?.nextCursor ?? null);
         } catch (error) {
            if (generation !== fetchGenerationRef.current) return;
            console.error("Error loading chat histories:", error);
            if (after) setNextCursor(null);
         } finally {
            if (generation === fetchGenerationRef.current) setLoading(false);
         }
      },
      [user?.id, studentId, loadMoreHistories, selectedBaseKnowledge.id]
   );

   useEffect(() => {
      if (isOpen && user?.id) fetchHistories();
   }, [isOpen, user?.id, fetchHistories]);
   useEffect(() => {
      if (shouldRefetchHistories) {
         fetchHistories({ keepLoaded: true });
         useStateHistoryChatPage.setState({ shouldRefetchHistories: false });
      }
   }, [shouldRefetchHistories, fetchHistories]);

   const renderSkeletonItems = useCallback(
      () =>
         Array(15)
            .fill(0)
            .map((_, index) => (
               <div key={"skeleton-" + index} className="p-2 rounded-lg">
                  <Skeleton className="h-5 w-[80%] mb-2" />
                  <Skeleton className="h-3 w-[60%]" />
               </div>
            )),
      []
   );
   const handleScroll = useCallback(
      (e: React.UIEvent<HTMLDivElement>) => {
         const target = e.target as HTMLDivElement;
         const previousScrollTop = previousScrollTopRef.current;
         previousScrollTopRef.current = target.scrollTop;
         if (!nextCursor || loading || !user?.id) return;
         if (target.scrollTop <= previousScrollTop) return;
         const scrollFromBottom =
            target.scrollHeight - target.scrollTop - target.clientHeight;
         if (scrollFromBottom < 100) fetchHistories({ after: nextCursor });
      },
      [nextCursor, loading, user?.id, fetchHistories]
   );
   const patchHistoryItem = useCallback(
      (updatedItem: Partial<ChatHistoryType>) => {
         if (!updatedItem.id) return;
         setHistoryList((currentHistoryList) =>
            currentHistoryList
               .map((item) =>
                  item.id === updatedItem.id ? { ...item, ...updatedItem } : item
               )
               .sort(compareChatHistoryOrder)
         );
         patchChatHistory(updatedItem);
         if (historyItem?.id === updatedItem.id) {
            setHistoryItem({ ...historyItem, ...updatedItem });
         }
      },
      [historyItem, setHistoryItem, patchChatHistory]
   );
   useEffect(() => {
      if (!pendingHistoryPatch) return;
      patchHistoryItem(pendingHistoryPatch);
      useStateHistoryChatPage.setState({ pendingHistoryPatch: null });
   }, [pendingHistoryPatch, patchHistoryItem]);
   const removeHistoryItem = useCallback(
      (removedId: string) => {
         setHistoryList((currentHistoryList) =>
            currentHistoryList.filter((historyItem) => historyItem.id !== removedId)
         );
         removeChatHistory(removedId);
      },
      [removeChatHistory]
   );

   const handleResizeStart = useCallback(
      (event: React.PointerEvent<HTMLDivElement>) => {
         if (isCompactLayout) return;
         event.preventDefault();
         event.currentTarget.setPointerCapture(event.pointerId);
         onResizeStateChange(true);
         document.body.style.cursor = "col-resize";
         document.body.style.userSelect = "none";
         let animationFrameId = 0;
         let lastWidth = sidebarWidth;
         const handleResizeMove = (moveEvent: PointerEvent) => {
            lastWidth = clampSidebarWidth(moveEvent.clientX);
            cancelAnimationFrame(animationFrameId);
            animationFrameId = requestAnimationFrame(() =>
               onSidebarWidthChange(lastWidth)
            );
         };
         const handleResizeEnd = () => {
            cancelAnimationFrame(animationFrameId);
            onSidebarWidthChange(lastWidth);
            onResizeStateChange(false);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
            window.removeEventListener("pointermove", handleResizeMove);
            window.removeEventListener("pointerup", handleResizeEnd);
            window.removeEventListener("pointercancel", handleResizeEnd);
         };
         window.addEventListener("pointermove", handleResizeMove);
         window.addEventListener("pointerup", handleResizeEnd);
         window.addEventListener("pointercancel", handleResizeEnd);
      },
      [isCompactLayout, onResizeStateChange, onSidebarWidthChange, sidebarWidth]
   );

   useEffect(() => {
      if (!(isMobile || isTablet) || !isOpen) return;
      const handleClickOutside = (event: MouseEvent) => {
         const target = event.target as HTMLElement;
         const rect = historyRef.current?.getBoundingClientRect();
         if (!rect) return;
         const inside =
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom;
         if (
            inside ||
            target.closest("[data-radix-popper-content-wrapper]") ||
            target.closest('[role="alertdialog"], [role="dialog"]')
         ) {
            return;
         }
         onOpenChange(false);
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
   }, [isMobile, isTablet, isOpen, onOpenChange]);

   const pinnedItems = historyList.filter((item) => item.pinnedAt);
   const recentItems = historyList.filter((item) => !item.pinnedAt);
   const pinLimitReached = pinnedItems.length >= MAX_PINNED_CHAT_HISTORIES;
   const toggleLabel = isOpen
      ? translate("closeChatHistory")
      : translate("openChatHistory");

   return (
      <div className={className}>
         <div
            ref={historyRef}
            className={cn(
               "fixed top-16 sm:top-20 bottom-0 left-0 z-20 flex flex-col border-r border-border",
               isCompactLayout
                  ? "bg-background shadow-lg"
                  : "bg-gray-50 dark:bg-gray-900",
               isResizing ? "" : "transition-all duration-300 ease-in-out"
            )}
            style={{ width: isOpen ? effectiveSidebarWidth : 64 }}
         >
            <div
               className={cn(
                  "flex p-2.5 pb-3 gap-2 shrink-0",
                  isOpen
                     ? "items-center justify-between px-3"
                     : "flex-col items-center justify-center"
               )}
            >
               <div
                  className={cn(
                     "flex items-center gap-1.5",
                     isOpen ? "flex-1 min-w-0" : "flex-col justify-center"
                  )}
               >
                  {!hiddenNewChat && (
                     <CustomTooltip
                        text={translate("newChat")}
                        position="right"
                        delayDuration={150}
                     >
                        <Button
                           type="button"
                           variant="outline"
                           size={isOpen ? "default" : "icon"}
                           onClick={() => {
                              resetState();
                              router.push("/");
                           }}
                           className={cn(
                              "h-9 rounded-full bg-transparent text-gray-600 hover:text-primary dark:text-gray-200 dark:hover:text-primary",
                              isOpen ? "flex-1 min-w-0" : "w-9 shrink-0"
                           )}
                           aria-label={translate("newChat")}
                        >
                           <PlusIcon className="h-4 w-4 shrink-0" />
                           {isOpen && (
                              <span className="truncate">{translate("newChat")}</span>
                           )}
                        </Button>
                     </CustomTooltip>
                  )}
                  <TemporaryChatToggle size="icon" />
                  <CustomTooltip
                     text={`${translate("searchChatHistory")} (Cmd+K / Ctrl+K)`}
                     position="right"
                     delayDuration={150}
                  >
                     <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 rounded-full flex items-center justify-center shrink-0"
                        aria-label={translate("searchChatHistory")}
                        onClick={() =>
                           useStateHistoryChatPage.getState().setSearchDialogOpen(true)
                        }
                     >
                        <Search className="h-4 w-4" />
                     </Button>
                  </CustomTooltip>
               </div>
               <CustomTooltip text={toggleLabel} position="right" delayDuration={150}>
                  <Button
                     type="button"
                     variant="ghost"
                     size="icon"
                     onClick={() => onOpenChange(!isOpen)}
                     className={cn(
                        "h-9 w-9 rounded-lg hover:bg-muted shrink-0",
                        !isOpen && "order-first"
                     )}
                     aria-label={toggleLabel}
                  >
                     {isOpen ? (
                        <ChevronLeft className="h-4 w-4" />
                     ) : (
                        <ChevronRight className="h-4 w-4" />
                     )}
                  </Button>
               </CustomTooltip>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">
               {isOpen && (
                  <ScrollArea
                     className="h-full pl-2 pr-2 [&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]>div]:!w-full"
                     onScrollCapture={handleScroll}
                  >
                     <div className="w-full min-w-0 pt-1 space-y-1 overflow-hidden">
                        {isTemporaryChat && (
                           <div className="mx-2 mb-3 p-3 rounded-lg bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700">
                              <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                                 <HatGlasses size={16} />
                                 <span className="text-xs font-medium">
                                    {translate("temporaryMode")}
                                 </span>
                              </div>
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                 {translate("temporaryModeDescription")}
                              </p>
                           </div>
                        )}
                        {[
                           {
                              key: "pinned",
                              label: translate("pinned"),
                              items: pinnedItems,
                              open: isPinnedSectionOpen,
                              toggle: () => setPinnedSectionOpen(!isPinnedSectionOpen),
                              pinDisabled: false
                           },
                           {
                              key: "recents",
                              label: translate("recents"),
                              items: recentItems,
                              open: isRecentSectionOpen,
                              toggle: () => setRecentSectionOpen(!isRecentSectionOpen),
                              pinDisabled: pinLimitReached
                           }
                        ].map(
                           (section) =>
                              section.items.length > 0 && (
                                 <div key={section.key}>
                                    <Button
                                       variant="ghost"
                                       size="sm"
                                       aria-expanded={section.open}
                                       onClick={section.toggle}
                                       className="group w-full justify-start gap-1.5 px-2 text-sm font-semibold text-foreground [&_svg]:size-3.5"
                                    >
                                       <span>{section.label}</span>
                                       <ChevronDown
                                          className={cn("transition-all", {
                                             "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100":
                                                section.open,
                                             "-rotate-90": !section.open
                                          })}
                                       />
                                       {!section.open && (
                                          <span className="font-normal text-muted-foreground">
                                             ({section.items.length})
                                          </span>
                                       )}
                                    </Button>
                                    {section.open &&
                                       section.items.map((item) => (
                                          <ChatHistoryItem
                                             key={item.id}
                                             item={item}
                                             shareKey={shareKey}
                                             studentId={studentId}
                                             pinDisabled={section.pinDisabled}
                                             onItemPatch={patchHistoryItem}
                                             onItemRemove={removeHistoryItem}
                                          />
                                       ))}
                                 </div>
                              )
                        )}
                        {loading &&
                           (historyList.length ? (
                              <div className="py-3">
                                 <Loading size="sm" />
                              </div>
                           ) : (
                              renderSkeletonItems()
                           ))}
                     </div>
                  </ScrollArea>
               )}
            </div>
            <div
               className={cn(
                  "flex border-t border-border shrink-0",
                  !isOpen
                     ? "flex-col items-center justify-center gap-1.5 py-2.5 px-2"
                     : "items-center justify-between px-3 py-2.5"
               )}
            >
               <div
                  className={cn(
                     "flex items-center",
                     !isOpen ? "flex-col justify-center gap-1.5" : "gap-1"
                  )}
               >
                  <Button
                     type="button"
                     variant={!isOpen ? "ghost" : "outline"}
                     size={!isOpen ? "icon" : "xs"}
                     className={cn(
                        "rounded-full cursor-auto",
                        !isOpen ? "h-9 w-9 p-0" : "px-2"
                     )}
                  >
                     <UsageIndicator compact iconOnly={!isOpen} />
                  </Button>
                  <ModeToggle />
                  <LanguageSwitcher />
               </div>
               <ProfileMenu />
            </div>
            {!isCompactLayout && isOpen && (
               <div
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize chat history sidebar"
                  className="absolute top-0 right-0 h-full w-2 translate-x-1 cursor-col-resize touch-none bg-transparent hover:bg-primary/20"
                  onPointerDown={handleResizeStart}
               />
            )}
         </div>
      </div>
   );
}
